/**
 * lib/DataContext.tsx
 * --------------------
 * What: Provides app-wide data access and caching for core entities (rooms,
 * items, etc.) around the Supabase client.
 * Why: Avoids prop-drilling and centralises fetch/update flows so screens stay
 * focused on presentation.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';
import { useAuth } from './AuthContext';
import { useFocusEffect } from '@react-navigation/native';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Category = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Location = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Document = any;

interface Room {
  id: string;
  name: string;
  description: string;
  created_at: string;
  items: Array<{ count: number }>;
  total_count?: number;
}

interface RoomDisplay {
  id: string;
  name: string;
  created_at: string;
}

interface FetchSortedRoomsParams {
  p_user_id: string;
  p_search: string;
  p_from: number;
  p_to: number;
}

interface FetchSortedRoomsResponse {
  data: Room[] | null;
  error: Error | null;
  count: number | null;
}

type Item = {
  id: string;
  name: string;
  description?: string;
  make?: string;
  model?: string;
  room_id: string;
  purchase_date?: string;
  price?: number;
  created_at: string;
  rooms: {
    name: string;
  };
  subcategory?: {
    id: string;
    name: string;
    category: {
      id: string;
      name: string;
    };
  };
  item_images?: {
    id: string;
    url: string;
    order?: number;
    updated_at: string;
  }[];
};

type DataContextType = {
  rooms: Room[];
  items: Item[];
  categories: Category[];
  locations: Location[];
  documents: Document[];
  lastSync: Date | null;
  isLoading: boolean;
  refreshRooms: () => Promise<void>;
  refreshItems: () => Promise<void>;
  refreshAll: () => Promise<void>;
  error: Error | null;
  isInitialized: boolean;
  totalValue: number;
  refreshTotalValue: () => Promise<void>;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [totalValue, setTotalValue] = useState<number>(0);
  
  // Only in-flight request prevention
  const isFetching = useRef<{ [key: string]: boolean }>({});
  const initialFetchDone = useRef(false);

  const fetchRooms = useCallback(async () => {
    if (!user || isFetching.current.rooms) return;
    isFetching.current.rooms = true;
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select(`
          id,
          name,
          description,
          created_at,
          items!items_room_id_fkey(count)
        `)
        .eq('user_id', user.id)
        .order('name');

      if (error) throw error;
      const roomsWithCount = data.map(room => ({
        ...room,
        item_count: room.items[0]?.count || 0
      }));
      setRooms(roomsWithCount);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      isFetching.current.rooms = false;
    }
  }, [user]);

  const fetchItems = useCallback(async () => {
    if (!user || isFetching.current.items) return;
    isFetching.current.items = true;
    try {
      const { data, error } = await supabase
        .from('items')
        .select(`
          id,
          name,
          description,
          room_id,
          purchase_date,
          price,
          created_at,
          rooms!items_room_id_fkey (
            name
          ),
          subcategory:item_subcategories!items_subcategory_id_fkey (
            id,
            name,
            category:item_categories (
              id,
              name
            )
          ),
          item_images (
            id,
            url,
            "order",
            updated_at
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .returns<Item[]>();

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      isFetching.current.items = false;
    }
  }, [user]);

  const fetchTotalValue = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('items')
        .select('price')
        .eq('user_id', user.id)
        .not('price', 'is', null);
      if (error) throw error;
      const total = (data || []).reduce((sum, item) => sum + (item.price || 0), 0);
      setTotalValue(total);
    } catch (error) {
      console.error('Error fetching total value:', error);
      setTotalValue(0);
    }
  }, [user]);

  const refreshRooms = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    await fetchRooms();
    setIsLoading(false);
  }, [user, fetchRooms]);

  const refreshItems = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    await fetchItems();
    setIsLoading(false);
  }, [user, fetchItems]);

  const refreshTotalValue = useCallback(async () => {
    await fetchTotalValue();
  }, [fetchTotalValue]);

  const refreshAll = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    await Promise.all([fetchRooms(), fetchItems(), fetchTotalValue()]);
    setIsLoading(false);
  }, [user, fetchRooms, fetchItems, fetchTotalValue]);

  // Initial data fetch
  useEffect(() => {
    if (user && !initialFetchDone.current) {
      initialFetchDone.current = true;
      refreshAll();
    }
  }, [user?.id, refreshAll]);

  // Reset state when user changes
  useEffect(() => {
    if (!user) {
      initialFetchDone.current = false;
      setRooms([]);
      setItems([]);
      setIsLoading(false);
    }
  }, [user]);

  // Add logging for state changes
  useEffect(() => {
    // Removed console.log for DataProvider state changed
  }, [isInitialized, isLoading, error, rooms, items, categories, locations, documents]);

  // Add logging for component lifecycle
  useEffect(() => {
    // Removed console.log for DataProvider mounted/unmounted
  }, []);

  const value = {
    rooms,
    items,
    categories,
    locations,
    documents,
    lastSync,
    isLoading,
    refreshRooms,
    refreshItems,
    refreshAll,
    error,
    isInitialized,
    totalValue,
    refreshTotalValue,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}

// Add new paginated items logic
export function usePaginatedItems() {
  const { user } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRoom, setFilterRoom] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'recent' | 'name'>('recent');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [totalItems, setTotalItems] = useState<number | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const PAGE_SIZE = 25;
  
  // Add ref to track if we're already fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  console.log('🔍 [usePaginatedItems] Hook called with state:', {
    page,
    search,
    filterRoom,
    sortBy,
    isLoading,
    itemsCount: items.length,
    refreshIndex
  });

  const fetchPage = useCallback(async (pageNum: number, reset = false) => {
    console.log('🚀 [usePaginatedItems] fetchPage called:', { pageNum, reset, user: !!user, isFetching: isFetchingRef.current });
    if (!user) {
      console.log('❌ [usePaginatedItems] No user, returning early');
      return;
    }
    
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      console.log('❌ [usePaginatedItems] Already fetching, skipping duplicate call');
      return;
    }
    
    isFetchingRef.current = true;
    console.log('⏳ [usePaginatedItems] Setting isLoading to true');
    setIsLoading(true);
    
    const from = (pageNum - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;
    
    console.log('📊 [usePaginatedItems] Query params:', { from, to, PAGE_SIZE });
    
    let query = supabase
      .from('items')
      .select(`
        id,
        name,
        description,
        id,
        name,
        description,
        room_id,
        purchase_date,
        price,
        created_at,
        rooms!items_room_id_fkey (
          name
        ),
        subcategory:item_subcategories!items_subcategory_id_fkey (
          id,
          name,
          category:item_categories (
            id,
            name
          )
        ),
        item_images (
          id,
          url,
          "order",
          updated_at
        )
      `, { count: 'exact' })
      .eq('user_id', user.id)
      .range(from, to);

    if (search) {
      console.log('🔍 [usePaginatedItems] Adding search filter:', search);
      query = query.ilike('name', `%${search}%`);
    }

    if (filterRoom) {
      console.log('🏠 [usePaginatedItems] Adding room filter:', filterRoom);
      query = query.eq('room_id', filterRoom);
    }

    if (sortBy === 'name') {
      console.log('📝 [usePaginatedItems] Sorting by name');
      query = query.order('name', { ascending: true });
    } else {
      console.log('📅 [usePaginatedItems] Sorting by created_at');
      query = query.order('created_at', { ascending: false });
    }

    console.log('🌐 [usePaginatedItems] Executing Supabase query...');
    const { data, error, count } = await query;

    if (error) {
      console.error('❌ [usePaginatedItems] Error fetching items:', error);
      setIsLoading(false);
      isFetchingRef.current = false;
      return;
    }

    console.log('✅ [usePaginatedItems] Query successful:', { 
      dataCount: data?.length || 0, 
      count, 
      hasError: !!error 
    });

    // Log raw database results for debugging image order
    console.log('🔍 [usePaginatedItems] Raw database results for items with images:');
    data?.forEach(item => {
      if (item.item_images && item.item_images.length > 0) {
        console.log(`📸 [usePaginatedItems] Item ${item.id} (${item.name}) raw images:`, 
          item.item_images.map(img => ({
            id: img.id,
            order: img.order,
            url: img.url,
            updated_at: img.updated_at
          }))
        );
      }
    });

    // Get total items count without filters
    console.log('📈 [usePaginatedItems] Fetching total items count...');
    const { count: totalItems } = await supabase
      .from('items')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    console.log('📊 [usePaginatedItems] Total items count:', totalItems);

    // Normalize rooms property to match Item type
    const normalized = (data || []).map(item => ({
      ...item,
      rooms: (Array.isArray(item.rooms) && item.rooms.length > 0)
        ? item.rooms[0]
        : (item.rooms && typeof item.rooms === 'object' && 'name' in item.rooms)
          ? item.rooms
          : { name: '-' }
    }));

    console.log('🔄 [usePaginatedItems] Setting state with normalized data:', {
      normalizedCount: normalized.length,
      hasMore: (data?.length || 0) === PAGE_SIZE
    });
    
    console.log('📊 [usePaginatedItems] Normalized items with images:', normalized.map(item => ({
      id: item.id,
      name: item.name,
      images: item.item_images?.map(img => ({ id: img.id, order: img.order, url: img.url, updated_at: img.updated_at }))
    })));

    setItems(normalized);
    setTotalCount(count || 0);
    setTotalItems(totalItems || 0);
    setHasMore((data?.length || 0) === PAGE_SIZE);
    
    console.log('✅ [usePaginatedItems] Setting isLoading to false');
    setIsLoading(false);
    isFetchingRef.current = false;
  }, [user, search, filterRoom, sortBy, refreshIndex]);

  const changePage = useCallback((newPage: number) => {
    console.log('📄 [usePaginatedItems] changePage called:', { newPage, currentPage: page });
    if (newPage < 1) {
      console.log('❌ [usePaginatedItems] Invalid page number, returning');
      return;
    }
    console.log('🔄 [usePaginatedItems] Setting page to:', newPage);
    setPage(newPage);
    console.log('🚀 [usePaginatedItems] Calling fetchPage with new page');
    fetchPage(newPage, true);
  }, [fetchPage, page]);

  const refresh = useCallback(async () => {
    console.log('🔄 [usePaginatedItems] refresh called, current page:', page);
    console.log('🔄 [usePaginatedItems] Current items before refresh:', items.map(item => ({
      id: item.id,
      name: item.name,
      images: item.item_images?.map(img => ({ id: img.id, order: img.order }))
    })));
    
    // Wait for any current fetch to complete
    while (isFetchingRef.current) {
      console.log('⏳ [usePaginatedItems] Waiting for current fetch to complete before refresh');
      await new Promise(resolve => setTimeout(resolve, 50)); // Wait 50ms and check again
    }
    
    const newRefreshIndex = refreshIndex + 1;
    console.log('📈 [usePaginatedItems] Incrementing refreshIndex to:', newRefreshIndex);
    setRefreshIndex(newRefreshIndex);
    console.log('🔄 [usePaginatedItems] Refresh index updated, useEffect will trigger fetch');
  }, [page, refreshIndex, items]);

  // Track if this is the initial focus
  const isInitialFocus = useRef(true);

  // Fetch when screen is focused - but only on initial focus, not on page changes
  useFocusEffect(
    useCallback(() => {
      console.log('👁️ [usePaginatedItems] Screen focused, isInitialFocus:', isInitialFocus.current);
      if (isInitialFocus.current) {
        console.log('👁️ [usePaginatedItems] Initial focus, calling fetchPage:', { page });
        fetchPage(page, true);
        isInitialFocus.current = false;
      } else {
        console.log('👁️ [usePaginatedItems] Not initial focus, skipping fetchPage call');
      }
    }, [fetchPage, page])
  );

  // Avoid resetting to page 1 on the very first mount (which would override a
  // page number restored from route params). We only reset when the user
  // actually changes search / filter / sort after initial render.
  const didMountRef = useRef(false);
  // Track previous search / filter / sort so we can detect genuine changes
  const prevFiltersRef = useRef<{
    search: string;
    filterRoom: string | null;
    sortBy: typeof sortBy;
  }>({ search, filterRoom, sortBy });

  // Keep a ref to the latest fetchPage so we can call it without putting it in the
  // dependency array (avoids unnecessary re-triggers when the function identity
  // changes for unrelated reasons, e.g. user/sign-in events)
  const fetchPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchPageRef.current = fetchPage;
  }, [fetchPage]);

  useEffect(() => {
    // Skip the very first render
    if (!didMountRef.current) {
      didMountRef.current = true;
      // Update snapshot of filters
      prevFiltersRef.current = { search, filterRoom, sortBy };
      return;
    }

    const prev = prevFiltersRef.current;
    // Only proceed if at least one of the filter values actually changed
    const filtersChanged =
      prev.search !== search ||
      prev.filterRoom !== filterRoom ||
      prev.sortBy !== sortBy;

    if (!filtersChanged) {
      // Bail out if nothing truly changed (e.g. effect triggered by function identity change)
      return;
    }

    console.log('🔄 [usePaginatedItems] Search/filter/sort changed:', { search, filterRoom, sortBy });
    console.log('📄 [usePaginatedItems] Resetting page to 1');
    setPage(1);
    console.log('🚀 [usePaginatedItems] Calling fetchPage with page 1');
    fetchPageRef.current(1, true);

    // Update snapshot for next comparison
    prevFiltersRef.current = { search, filterRoom, sortBy };
  }, [search, filterRoom, sortBy]);

  // Fetch when explicitly refreshed
  useEffect(() => {
    if (refreshIndex > 0) {
      console.log('🔄 [usePaginatedItems] Refresh triggered by refreshIndex:', refreshIndex);
      console.log('🚀 [usePaginatedItems] Calling fetchPage for explicit refresh');
      fetchPageRef.current(page, true);
    }
  }, [refreshIndex, page]);

  console.log('📤 [usePaginatedItems] Returning hook values:', {
    itemsCount: items.length,
    isLoading,
    hasMore,
    page,
    totalCount,
    totalItems
  });

  return {
    items,
    isLoading,
    hasMore,
    search,
    setSearch,
    filterRoom,
    setFilterRoom,
    sortBy,
    setSortBy,
    totalCount,
    totalItems,
    page,
    PAGE_SIZE,
    refresh,
    changePage,
    fetchPage
  };
}

// Add new paginated rooms logic
export function usePaginatedRooms() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'name'>('name');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const PAGE_SIZE = 25;
  const [refreshIndex, setRefreshIndex] = useState(0);
  
  // Add ref to track if we're already fetching to prevent duplicate calls
  const isFetchingRef = useRef(false);

  console.log('🏠 [usePaginatedRooms] Hook called with state:', {
    page,
    search,
    sortBy,
    isLoading,
    roomsCount: rooms.length,
    refreshIndex
  });

  const fetchPage = useCallback(async (pageNum: number, reset = false) => {
    console.log('🚀 [usePaginatedRooms] fetchPage called:', { pageNum, reset, user: !!user, isFetching: isFetchingRef.current });
    if (!user) {
      console.log('❌ [usePaginatedRooms] No user, returning early');
      return;
    }
    
    // Prevent duplicate fetches
    if (isFetchingRef.current) {
      console.log('❌ [usePaginatedRooms] Already fetching, skipping duplicate call');
      return;
    }
    
    isFetchingRef.current = true;
    console.log('⏳ [usePaginatedRooms] Setting isLoading to true');
    setIsLoading(true);
    
    const from = (pageNum - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    console.log('📊 [usePaginatedRooms] Query params:', { from, to, PAGE_SIZE });

    try {
      let query = supabase
        .from('rooms')
        .select(`
          id,
          name,
          description,
          created_at,
          items!items_room_id_fkey(count)
        `, { count: 'exact' })
        .eq('user_id', user.id)
        .range(from, to);

      if (search) {
        console.log('🔍 [usePaginatedRooms] Adding search filter:', search);
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (sortBy === 'name') {
        console.log('📝 [usePaginatedRooms] Sorting by name');
        query = query.order('name', { ascending: true }).order('id', { ascending: true });
      } else {
        console.log('📅 [usePaginatedRooms] Sorting by created_at');
        query = query.order('created_at', { ascending: false }).order('id', { ascending: true });
      }

      console.log('🌐 [usePaginatedRooms] Executing Supabase query...');
      const { data, error, count } = await query;

      if (error) {
        console.error('❌ [usePaginatedRooms] Error fetching paginated rooms:', error);
        setIsLoading(false);
        isFetchingRef.current = false;
        return;
      }

      console.log('✅ [usePaginatedRooms] Query successful:', { 
        dataCount: data?.length || 0, 
        count, 
        hasError: !!error 
      });

      const roomData = data?.map((room: Room): RoomDisplay => ({
        id: room.id.substring(0, 8),
        name: room.name,
        created_at: new Date(room.created_at).toISOString().split('T')[0]
      }));

      // Normalize item_count
      const normalized = (data || []).map((room: Room) => ({
        ...room,
        item_count: room.items && room.items[0] ? room.items[0].count : 0
      }));
      
      console.log('🔄 [usePaginatedRooms] Setting state with normalized data:', {
        normalizedCount: normalized.length,
        hasMore: Boolean(data && data.length === PAGE_SIZE)
      });
      
      setRooms(normalized);
      setTotalCount(count ?? null);
      setHasMore(Boolean(data && data.length === PAGE_SIZE));
    } catch (error) {
      console.error('❌ [usePaginatedRooms] Error in fetchPage:', error);
    } finally {
      console.log('✅ [usePaginatedRooms] Setting isLoading to false');
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [user, search, sortBy, refreshIndex]);

  const changePage = useCallback((newPage: number) => {
    console.log('📄 [usePaginatedRooms] changePage called:', { newPage, currentPage: page });
    if (newPage < 1) {
      console.log('❌ [usePaginatedRooms] Invalid page number, returning');
      return;
    }
    console.log('🔄 [usePaginatedRooms] Setting page to:', newPage);
    setPage(newPage);
    console.log('🚀 [usePaginatedRooms] Calling fetchPage with new page');
    fetchPage(newPage, true);
  }, [fetchPage, page]);

  const loadMore = useCallback(() => {
    console.log('📄 [usePaginatedRooms] loadMore called:', { isLoading, hasMore });
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      console.log('🔄 [usePaginatedRooms] Loading more, setting page to:', nextPage);
      setPage(nextPage);
    }
  }, [isLoading, hasMore, page]);

  const refresh = useCallback(() => {
    console.log('🔄 [usePaginatedRooms] refresh called, current page:', page);
    const newRefreshIndex = refreshIndex + 1;
    console.log('📈 [usePaginatedRooms] Incrementing refreshIndex to:', newRefreshIndex);
    setRefreshIndex(newRefreshIndex);
  }, [page, refreshIndex]);

  // Track if this is the initial focus
  const isInitialFocus = useRef(true);

  // Track previous search / sort to detect genuine changes
  const prevRoomFiltersRef = useRef<{ search: string; sortBy: typeof sortBy }>({ search, sortBy });

  // Ref for latest fetchPage to avoid effect dependency
  const fetchRoomsPageRef = useRef(fetchPage);
  useEffect(() => {
    fetchRoomsPageRef.current = fetchPage;
  }, [fetchPage]);

  // Fetch when screen is focused - but only on initial focus, not on page changes
  useFocusEffect(
    useCallback(() => {
      console.log('👁️ [usePaginatedRooms] Screen focused, isInitialFocus:', isInitialFocus.current);
      if (isInitialFocus.current) {
        console.log('👁️ [usePaginatedRooms] Initial focus, calling fetchPage:', { page });
        fetchRoomsPageRef.current(page, true);
        isInitialFocus.current = false;
      } else {
        console.log('👁️ [usePaginatedRooms] Not initial focus, skipping fetchPage call');
      }
    }, [page])
  );

  // Skip resetting on initial mount; only react to real user changes.
  const didRoomsMountRef = useRef(false);

  useEffect(() => {
    if (!didRoomsMountRef.current) {
      didRoomsMountRef.current = true;
      prevRoomFiltersRef.current = { search, sortBy };
      return; // skip first run
    }

    const prev = prevRoomFiltersRef.current;
    const filtersChanged = prev.search !== search || prev.sortBy !== sortBy;
    if (!filtersChanged) {
      return;
    }

    console.log('🔄 [usePaginatedRooms] Search/sort changed:', { search, sortBy });
    console.log('📄 [usePaginatedRooms] Resetting page to 1');
    setPage(1);
    console.log('🚀 [usePaginatedRooms] Calling fetchPage with page 1');
    fetchRoomsPageRef.current(1, true);

    prevRoomFiltersRef.current = { search, sortBy };
  }, [search, sortBy]);

  // Fetch when explicitly refreshed
  useEffect(() => {
    if (refreshIndex > 0) {
      console.log('🔄 [usePaginatedRooms] Refresh triggered by refreshIndex:', refreshIndex);
      console.log('�� [usePaginatedRooms] Calling fetchPage for explicit refresh');
      fetchRoomsPageRef.current(page, true);
    }
  }, [refreshIndex, page]);

  console.log('📤 [usePaginatedRooms] Returning hook values:', {
    roomsCount: rooms.length,
    isLoading,
    hasMore,
    page,
    totalCount
  });

  return {
    rooms,
    isLoading,
    hasMore,
    loadMore,
    search,
    setSearch,
    sortBy,
    setSortBy,
    totalCount,
    page,
    PAGE_SIZE,
    refresh,
    changePage
  };
} 